import { defineStore } from 'pinia';
import { httpAPI } from '@/services/ajax';
import { useAlertStore } from './alert';
import { useUserStore } from './user';
import { shouldShowError } from '@/utils/errorHelper';
import { getViewType } from '@/utils/SiteAccessUtils';
import { provinceOptions } from '@/services/constants';
import { normalizeGlobalRegionSelection } from '@/components/Region/region';
import app from '@/main.js';

const GLOBAL_SITE_KEY = 'globalSiteId';
const GLOBAL_REGION_KEY = 'globalRegion';

export const useSiteStore = defineStore('site', {
    state: () => ({
        sites: [],
        areas: [],

        fetching: false,
        fetchPromise: null,
        fetchingAreas: false,
        fetchAreasPromise: null,

        globalSiteId: null,
        globalRegion: null,
    }),
    actions: {
        fetchSites() {
            // If we're already fetching, return the actual fetching promise
            if (this.fetching && this.fetchPromise) {
                return this.fetchPromise;
            }

            this.fetching = true;
            const url =
                app.config.globalProperties.$env.apiRoot + 'company-site/read';
            const vm = this;

            this.fetchPromise = httpAPI(
                url,
                null,
                'GET',
                app.config.globalProperties.$router
            )
                .then((response) => {
                    if (response.success) {
                        vm.sites = response.data || [];

                        // drop stored site if it no longer exists
                        if (
                            vm.globalSiteId &&
                            !vm.sites.find(
                                (s) => String(s.id) === String(vm.globalSiteId)
                            )
                        ) {
                            vm.setGlobalSiteId(null);
                        }
                    } else if (shouldShowError(response)) {
                        const alertStore = useAlertStore();
                        alertStore.pushError(
                            'Failed to fetch sites: ' +
                                (response?.message || 'Unknown error')
                        );
                    }
                    return vm.sites;
                })
                .catch((error) => {
                    const alertStore = useAlertStore();
                    alertStore.pushError(
                        'Unable to load sites. Please refresh the page.' +
                            (error?.message ? ': ' + error.message : '')
                    );
                    vm.sites = [];
                    return vm.sites;
                })
                .finally(() => {
                    vm.fetching = false;
                    vm.fetchPromise = null;
                });

            return this.fetchPromise;
        },
        syncSites() {
            if (this.sites && this.sites.length > 0) {
                return Promise.resolve(this.sites);
            }
            return this.fetchSites();
        },
        clear({ preserveGlobalSiteSelection = false } = {}) {
            this.sites = [];
            this.areas = [];
            this.fetching = false;
            this.fetchPromise = null;
            this.fetchingAreas = false;
            this.fetchAreasPromise = null;

            if (!preserveGlobalSiteSelection) {
                this.setGlobalSiteId(null);
                this.setGlobalRegion(null);
            }
        },

        // Fetch all areas (company-wide)
        fetchAreas() {
            // If we're already fetching, return the actual fetching promise
            if (this.fetchingAreas && this.fetchAreasPromise) {
                return this.fetchAreasPromise;
            }

            this.fetchingAreas = true;
            const url =
                app.config.globalProperties.$env.apiRoot + 'audit-area/read';
            const vm = this;

            this.fetchAreasPromise = httpAPI(
                url,
                null,
                'GET',
                app.config.globalProperties.$router
            )
                .then((response) => {
                    if (response.success) {
                        vm.areas = response.data || [];
                    } else if (shouldShowError(response)) {
                        const alertStore = useAlertStore();
                        alertStore.pushError(
                            'Failed to fetch areas: ' +
                                (response?.message || 'Unknown error')
                        );
                    }
                    return vm.areas;
                })
                .catch((error) => {
                    const alertStore = useAlertStore();
                    alertStore.pushError(
                        'Unable to load areas. Please refresh the page.' +
                            (error?.message ? ': ' + error.message : '')
                    );
                    vm.areas = [];
                    return vm.areas;
                })
                .finally(() => {
                    vm.fetchingAreas = false;
                    vm.fetchAreasPromise = null;
                });

            return this.fetchAreasPromise;
        },

        // Fetch areas for a specific site (areas are company-wide, not site-specific)
        async fetchAreasForSite(siteId = null) {
            try {
                const url =
                    app.config.globalProperties.$env.apiRoot +
                    'audit-area/read';
                // Areas are company-wide, so we don't need to filter by siteId
                const payload = {};

                const response = await httpAPI(
                    url,
                    payload,
                    'GET',
                    app.config.globalProperties.$router
                );

                if (response?.success) {
                    this.areas = response.data || [];
                    return this.areas;
                } else if (shouldShowError(response)) {
                    const alertStore = useAlertStore();
                    alertStore.pushError(
                        'Failed to fetch areas: ' +
                            (response?.message || 'Unknown error')
                    );
                }
                return [];
            } catch (error) {
                const alertStore = useAlertStore();
                alertStore.pushError('Error fetching areas');
                return [];
            }
        },

        syncAreas() {
            if (this.areas && this.areas.length > 0) {
                return Promise.resolve(this.areas);
            }
            return this.fetchAreas();
        },

        // Set the global site ID
        setGlobalSiteId(siteId = null) {
            this.globalSiteId = siteId;
            if (siteId === null) localStorage.removeItem(GLOBAL_SITE_KEY);
            else {
                localStorage.setItem(GLOBAL_SITE_KEY, String(siteId));
            }
        },

        setGlobalRegion(region = null) {
            const normalizedRegion = normalizeGlobalRegionSelection(region);

            this.globalRegion = normalizedRegion;
            if (normalizedRegion) {
                localStorage.setItem(
                    GLOBAL_REGION_KEY,
                    JSON.stringify(normalizedRegion)
                );
                return;
            }

            localStorage.removeItem(GLOBAL_REGION_KEY);
        },

        // init global site id
        initGlobalSite() {
            //hydrate from storage
            const stored = localStorage.getItem(GLOBAL_SITE_KEY);
            this.globalSiteId = stored ?? null;
        },

        initGlobalRegion() {
            const stored = localStorage.getItem(GLOBAL_REGION_KEY);
            if (!stored) {
                this.globalRegion = null;
                return;
            }

            try {
                const parsed = JSON.parse(stored);
                this.setGlobalRegion(parsed);
            } catch {
                this.setGlobalRegion(null);
            }
        },

        // site initiated hook
        initializeSiteStore() {
            this.initGlobalSite();
            this.initGlobalRegion();
            return this.syncSites();
        },
    },
    getters: {
        getSiteOptions(state) {
            return state.sites.map((site) => ({
                text: site.name,
                value: String(site.id),
            }));
        },

        getProvinceOptions(state) {
            const provinces = [
                ...new Set(
                    state.sites
                        .map((site) => site?.province)
                        .filter(
                            (province) =>
                                province && String(province).trim().length > 0
                        )
                ),
            ].sort();

            return provinceOptions.filter((province) => {
                return provinces.includes(province.value);
            });
        },

        getAreaOptions(state) {
            return state.areas.map((area) => ({
                text: area.name,
                value: area.id,
            }));
        },

        // Get areas by site ID (if areas have siteId property)
        getAreasBySite: (state) => (siteId) => {
            return state.areas.filter(
                (area) => area.siteId === parseInt(siteId)
            );
        },

        // Find site by ID
        getSiteById: (state) => (id) => {
            return state.sites.find((site) => site.id === parseInt(id));
        },

        // Find area by ID
        getAreaById: (state) => (id) => {
            return state.areas.find((area) => area.id === parseInt(id));
        },

        // Get user's site IDs as strings
        userSiteIds() {
            return this.userSites.map((site) =>
                typeof site === 'object' ? site?.id : site
            );
        },

        // Get user's sites as objects
        userSites() {
            const userStore = useUserStore();
            if (userStore.isCompanyOwner && this.sites.length > 0) {
                return this.sites;
            }
            return userStore.user?.sites || [];
        },

        // Current view type: 'multi-site' | 'selected-site' | 'single-site-user'
        viewType(state) {
            const userStore = useUserStore();
            return getViewType(
                state.globalSiteId,
                this.userSiteIds.length,
                userStore.isCompanyOwner,
                this.sites.length
            );
        },

        /**
         * Is multi-site view active
         */
        isMultiSiteView() {
            return this.viewType === 'multi-site';
        },

        /**
         * Is selected-site view active
         */
        isSelectedSiteView() {
            return this.viewType === 'selected-site';
        },

        /**
         * Is single-site-user view active
         */
        isSingleSiteUserView() {
            return this.viewType === 'single-site-user';
        },

        /**
         * Does user have full site access (App Admin or Company Owner)
         */
        hasFullSiteAccess() {
            const userStore = useUserStore();
            return userStore.isAppAdmin || userStore.isCompanyOwner;
        },
    },
});
